/**
 * Los esquemas de entrada de la API.
 *
 * Se definen una vez y los usan los dos lados: el Worker para **rechazar** y el
 * formulario para **avisar**. Una regla que solo vive en el formulario no es
 * una regla — el navegador es del atacante.
 */

import { z } from 'zod';
import {
  ALLOCATION_MODE, DISCOUNT_TYPE, INCIDENT_SEVERITY, INVOICE_STATUS, PAYMENT_METHOD,
  PREFERRED_CHANNEL, PRICE_MODE, REQUEST_CATEGORY, TAX_ID_TYPE, UNIT_TYPE,
} from './enums.js';

export const uuid = z.uuid();
/**
 * Las filas creadas por la app usan UUID, pero el histórico conciliado conserva
 * identificadores estables como `hl-…` y `cp-…`. Son claves reales de D1, no
 * texto libre escrito por la persona. Exigir UUID acá dejaba justamente esos
 * cobros antiguos imposibles de pagar desde la interfaz.
 */
export const recordId = z.string().trim().min(1).max(120)
  .regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/, 'identificador inválido');
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'fecha inválida (YYYY-MM-DD)');
export const timestamp = z.iso.datetime();

/**
 * Un correo, normalizado antes de validarse.
 *
 * Se recorta y se pasa a minúsculas **primero** y se valida después: si no,
 * `" Ana@Correo.CR "` se rechaza por los espacios y la persona no entiende por
 * qué. La base lo guarda en una columna `collate nocase`, así que la
 * normalización acá y la comparación allá dicen lo mismo.
 */
export const email = z.string().trim().toLowerCase().pipe(z.email('correo inválido'));

/** Un correo opcional donde el campo vacío del formulario significa «ninguno». */
export const optionalEmail = z.union([
  z.literal('').transform(() => null),
  z.null(),
  z.undefined().transform(() => null),
  email,
]);

/** El dinero cruza como texto de céntimos. */
export const minor = z.union([
  z.string().regex(/^-?\d+$/, 'monto inválido').transform((s) => Number.parseInt(s, 10)),
  z.number().int(),
]);

export const positiveMinor = minor.refine((n) => n > 0, 'el monto debe ser mayor que cero');
export const nonNegativeMinor = minor.refine((n) => n >= 0, 'el monto no puede ser negativo');

const trimmed = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) => trimmed(max).optional().or(z.literal('').transform(() => undefined));

/* ── Sesión ───────────────────────────────────────────────────────────────── */

export const loginInput = z.object({
  email,
  password: z.string().min(1, 'ingresá tu contraseña'),
});
export type LoginInput = z.infer<typeof loginInput>;

/** Mínimo ocho caracteres, con mayúscula, minúscula, número y símbolo. */
export const passwordRule = z.string()
  .min(8, 'usá al menos 8 caracteres')
  .max(200, 'demasiado larga')
  .refine((v) => /[A-Z]/.test(v), 'incluí al menos una mayúscula')
  .refine((v) => /[a-z]/.test(v), 'incluí al menos una minúscula')
  .refine((v) => /[0-9]/.test(v), 'incluí al menos un número')
  .refine((v) => /[^A-Za-z0-9]/.test(v), 'incluí al menos un símbolo');

export const acceptInviteInput = z.object({
  token: z.string().min(20),
  password: passwordRule,
});

export const changePasswordInput = z.object({
  current_password: z.string().min(1, 'ingresá tu contraseña actual'),
  new_password: passwordRule,
});

export const requestResetInput = z.object({ email });

export const resetPasswordInput = z.object({
  token: z.string().min(20),
  password: passwordRule,
});

/* ── Consultorios y personas ──────────────────────────────────────────────── */

export const serviceUnitInput = z.object({
  unit_code: trimmed(20).min(1, 'el código es obligatorio'),
  unit_type: z.enum(UNIT_TYPE).default('CLINIC'),
  name: optionalText(160),
  location_id: uuid.nullish(),
  address: optionalText(240),
  notes: optionalText(2000),
  start_date: isoDate.nullish(),
});
export type ServiceUnitInput = z.infer<typeof serviceUnitInput>;

export const profileInput = z.object({
  full_name: trimmed(160).min(1, 'el nombre es obligatorio'),
  // `.optional()` además de la unión: un `union` con `z.undefined()` adentro
  // solo cubre la clave PRESENTE con valor `undefined» — un JSON nunca manda
  // eso, manda la clave AUSENTE, y Zod exige `.optional()` en la propiedad
  // del objeto para aceptar esa ausencia (bug real, encontrado 2-sep-2026).
  email: optionalEmail.optional(),
  phone: optionalText(40),
  preferred_channel: z.enum(PREFERRED_CHANNEL).default('WHATSAPP'),
});

export const unitMemberInput = z.object({
  service_unit_id: uuid,
  profile_id: uuid,
  role: z.enum(['MEMBER', 'UNIT_ADMIN']).default('MEMBER'),
  active_from: isoDate,
  notes: optionalText(500),
});

export const billingProfileInput = z.object({
  party_id: uuid,
  legal_name: trimmed(200).min(1, 'la razón social es obligatoria'),
  tax_id: optionalText(30),
  tax_id_type: z.enum(TAX_ID_TYPE).nullish(),
  // Ver el comentario de `profileInput.email` — mismo bug, mismo arreglo.
  billing_email: optionalEmail.optional(),
  phone: optionalText(40),
  address: optionalText(240),
  is_default: z.boolean().default(false),
  invoice_required: z.boolean().default(true),
}).refine(
  (v) => (v.tax_id == null || v.tax_id === '') === (v.tax_id_type == null),
  { message: 'la cédula y su tipo van juntos: o ambos, o ninguno', path: ['tax_id_type'] },
);

/* ── Contratos y reparto ──────────────────────────────────────────────────── */

export const subscriptionInput = z.object({
  service_unit_id: uuid,
  service_id: uuid,
  start_date: isoDate,
  end_date: isoDate.nullish(),
  billing_day: z.coerce.number().int().min(1).max(28).default(1),
  // El día 5 es el último día a tiempo: desde el 6 la mensualidad está vencida.
  due_days: z.coerce.number().int().min(0).max(90).default(4),
  price_mode: z.enum(PRICE_MODE).default('BASE_PRICE'),
  price_override: nonNegativeMinor.nullish(),
  /* El descuento va en dos campos separados por unidad de medida, igual que en
     la base: un `50` suelto podría ser «50%» o «₡0,50», y esa ambigüedad con
     dinero en enteros termina en un cobro mal emitido. */
  discount_type: z.enum(DISCOUNT_TYPE).default('NONE'),
  discount_percent: z.coerce.number().min(0).max(100).default(0),
  discount_amount: nonNegativeMinor.default(0),
  tax_rate: z.coerce.number().min(0).max(0.9999).default(0.13),
  notes: optionalText(2000),
})
  .refine((v) => (v.price_mode === 'PRICE_OVERRIDE') === (v.price_override != null), {
    message: 'el precio propio va con el modo «precio propio»', path: ['price_override'],
  })
  .refine(
    (v) => (v.discount_type === 'PERCENTAGE') === (v.discount_percent > 0),
    { message: 'un descuento porcentual necesita su porcentaje', path: ['discount_percent'] },
  )
  .refine(
    (v) => (v.discount_type === 'FIXED') === (v.discount_amount > 0),
    { message: 'un descuento de monto fijo necesita su monto', path: ['discount_amount'] },
  )
  .refine((v) => v.end_date == null || v.end_date >= v.start_date, {
    message: 'la fecha de fin no puede ser anterior al inicio', path: ['end_date'],
  });
export type SubscriptionInput = z.infer<typeof subscriptionInput>;

export const allocationMemberInput = z.object({
  party_id: uuid,
  billing_profile_id: uuid.nullish(),
  ordinal: z.coerce.number().int().positive(),
  percentage: z.coerce.number().gt(0).max(100).nullish(),
  fixed_amount: nonNegativeMinor.nullish(),
  rotation_seq: z.coerce.number().int().min(0).nullish(),
});

/**
 * Las sumas se validan aquí y **otra vez** en el dominio antes de escribir.
 * Duplicar la regla es a propósito: esta capa da el mensaje bonito, la otra da
 * la garantía.
 */
/**
 * SPLIT y ROTATION son las dos formas de repartir un cobro; qué campo llena
 * cada integrante decide el cálculo, no un tercer modo:
 *   - todos con `percentage`  → por porcentaje, deben sumar 100%
 *   - todos con `fixed_amount` → por monto fijo (se valida contra el subtotal
 *     en el dominio, que es quien conoce el precio del período)
 *   - ninguno de los dos       → por partes iguales
 * Mezclar campos entre integrantes de un mismo reparto no tiene una lectura
 * clara («¿el resto se reparte lo que sobra del monto fijo, en partes
 * iguales o por porcentaje?»), así que se exige un solo criterio a la vez.
 */
export const replaceAllocationInput = z.object({
  subscription_id: uuid,
  effective_from: isoDate,
  mode: z.enum(ALLOCATION_MODE),
  rotation_anchor: isoDate.nullish(),
  notes: optionalText(500),
  members: z.array(allocationMemberInput).min(1, 'la distribución necesita al menos un pagador'),
})
  .refine((v) => v.mode !== 'ROTATION' || v.rotation_anchor != null, {
    message: 'la rotación necesita un mes de inicio', path: ['rotation_anchor'],
  })
  .refine(
    (v) => v.mode !== 'SPLIT' || v.members.every((m) => m.percentage == null) || v.members.every((m) => m.percentage != null),
    { message: 'o todos los pagadores tienen porcentaje, o ninguno', path: ['members'] },
  )
  .refine(
    (v) => v.mode !== 'SPLIT' || v.members.every((m) => m.fixed_amount == null) || v.members.every((m) => m.fixed_amount != null),
    { message: 'o todos los pagadores tienen monto fijo, o ninguno', path: ['members'] },
  )
  .refine(
    (v) => {
      if (v.mode !== 'SPLIT' || !v.members.every((m) => m.percentage != null)) return true;
      const sum = v.members.reduce((a, m) => a + (m.percentage ?? 0), 0);
      return Math.abs(sum - 100) < 0.0001;
    },
    { message: 'los porcentajes deben sumar 100%', path: ['members'] },
  );
export type ReplaceAllocationInput = z.infer<typeof replaceAllocationInput>;

/* ── Facturación ──────────────────────────────────────────────────────────── */

export const periodInput = z.object({
  period_start: isoDate,
  subscription_ids: z.array(uuid).nullish(),
});

export const voidChargeInput = z.object({
  reason: trimmed(500).min(3, 'explicá por qué se anula'),
});

export const periodOverrideInput = z.object({
  subscription_id: uuid,
  period_start: isoDate,
  action: z.enum(['SKIP', 'PRICE']),
  price_override: nonNegativeMinor.nullish(),
  note: optionalText(500),
}).refine((v) => (v.action === 'PRICE') === (v.price_override != null), {
  message: 'cambiar el precio del mes requiere un monto', path: ['price_override'],
});

/* ── Pagos ────────────────────────────────────────────────────────────────── */

export const submitReceiptInput = z.object({
  charge_payer_id: recordId,
  amount: positiveMinor,
  method: z.enum(PAYMENT_METHOD),
  paid_on: isoDate.nullish(),
  reference: optionalText(120),
});

export const confirmPaymentInput = z.object({
  amount: positiveMinor.nullish(),
  paid_at: timestamp.nullish(),
  method: z.enum(PAYMENT_METHOD).nullish(),
});

export const rejectPaymentInput = z.object({
  reason: trimmed(500).min(3, 'explicá por qué se rechaza'),
});

export const manualPaymentInput = z.object({
  charge_payer_id: recordId,
  amount: positiveMinor,
  paid_at: timestamp,
  method: z.enum(PAYMENT_METHOD),
  reference: optionalText(120),
});

export const refundInput = z.object({
  reason: trimmed(500).min(3, 'explicá el motivo del reintegro'),
});

export const invoiceCallbackInput = z.object({
  invoice_request_id: uuid,
  status: z.enum(INVOICE_STATUS),
  external_ref: optionalText(120),
  clave_numerica: optionalText(80),
  consecutivo: optionalText(40),
  pdf_url: z.url().nullish(),
  error_message: optionalText(500),
});

/* ── NFC y solicitudes ────────────────────────────────────────────────────── */

export const serviceRequestInput = z.object({
  category: z.enum(REQUEST_CATEGORY),
  note: optionalText(500),
  contact_name: optionalText(120),
  contact_phone: optionalText(40),
  nonce: z.string().min(10),
});

export const incidentInput = z.object({
  service_unit_id: uuid,
  title: trimmed(160).min(3, 'contá qué pasó'),
  detail: optionalText(2000),
  severity: z.enum(INCIDENT_SEVERITY).default('NORMAL'),
});

/* ── Invitación a un consultorio ──────────────────────────────────────────── */

/**
 * Un enlace invita a un MÉDICO, y nada más.
 *
 * Antes se podía elegir «Administrador» acá (8-set-2026: se quitó). El enlace
 * es un token al portador que vive una semana, viaja en el query string y lo
 * canjea quien lo abra: si además otorgara `UNIT_ADMIN`, quien lo abriera
 * podría invitar a más gente y sacar del consultorio al médico que lo generó
 * —la guarda de «el consultorio necesita un administrador» la satisface él
 * mismo—. Subir a alguien a administrador lo sigue haciendo Clean Blue, que es
 * la única lista que no se reenvía por WhatsApp.
 */
export const unitInviteCreateInput = z.object({
  role: z.literal('MEMBER').default('MEMBER'),
});
export type UnitInviteCreateInput = z.infer<typeof unitInviteCreateInput>;

/**
 * Unirse por enlace o QR. `email`/`password` van opcionales porque quien ya
 * tiene sesión abierta no manda ninguno de los dos — el endpoint decide cuál
 * caso es el suyo y exige a mano lo que falte (`routes/invite-unit.ts`).
 */
export const unitInviteJoinInput = z.object({
  full_name: trimmed(160).optional(),
  email: email.optional(),
  password: passwordRule.optional(),
});
export type UnitInviteJoinInput = z.infer<typeof unitInviteJoinInput>;

/* ── Paginación ───────────────────────────────────────────────────────────── */

export const pageInput = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(200).optional(),
});
